import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Mic, X } from "lucide-react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { canUseRealtimeVoice } from "@/lib/tenant-config";
import {
  fetchWithAuth,
  type AccessTokenGetter,
} from "@/lib/utils";
import { useBackendReconnectKey } from "@/hooks/use-backend-connectivity";
import i18n from "@/lib/i18n";

type VoiceState = "checking" | "idle" | "connecting" | "active" | "unavailable";
const REALTIME_VOICE_AGENT_ID = "voice-agent";
const END_CONVERSATION_TOOL_NAME = "end_conversation";
const REALTIME_RECORDING_PROTOCOL = 1;
const REALTIME_RECORDING_PROXY_HEADER = "X-L9-Realtime-Recording-Proxy";
const TRANSCRIPT_CHECKPOINT_DELAY_MS = 350;
const TRANSCRIPT_BATCH_SIZE = 32;
const MAX_TRANSCRIPT_TEXT_LENGTH = 24_000;

type RecordingCloseReason =
  | "end_conversation"
  | "user_cancelled"
  | "disconnected"
  | "error";

interface RealtimeToolSchema {
  name: string;
  description: string | null;
  parameters: Record<string, unknown>;
}

interface RealtimeConfig {
  available: true;
  model: "gpt-realtime-2.1";
  voice: "marin";
  agent_id: typeof REALTIME_VOICE_AGENT_ID;
  instructions: string;
  tools: RealtimeToolSchema[];
  recording: RealtimeRecordingCapability;
}

interface RealtimeRecordingCapability {
  protocols: number[];
  transcript_capture: "client_realtime_events";
  tool_capture: "server_authoritative";
}

interface RealtimeRecordingInfo {
  protocol: 1;
  session_id: string;
  run_id: string;
  events_path: string;
  tools_path: string;
  close_path: string;
}

interface RealtimeToken {
  client_secret: string;
  model: "gpt-realtime-2.1";
  voice: "marin";
  recording: RealtimeRecordingInfo;
}

interface ToolResult {
  result?: unknown;
  error?: string | null;
  status: string;
}

interface RealtimeVoiceButtonProps {
  tenant: string | undefined;
  getToken: AccessTokenGetter;
  compact?: boolean;
  onRecordingCreated?: () => void | Promise<void>;
  onRecordingClosed?: () => void | Promise<void>;
}

interface CloseableSession {
  close(): void;
}

function closeRealtimeTransport(session: CloseableSession | null): void {
  try {
    session?.close();
  } catch {
    // Closing local WebRTC is best effort. The server-side recording close
    // still has to run so its durable envelope cannot be left active.
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isRealtimeConfig(value: unknown): value is RealtimeConfig {
  if (!isObject(value)) return false;
  return (
    value.available === true &&
    value.model === "gpt-realtime-2.1" &&
    value.voice === "marin" &&
    value.agent_id === REALTIME_VOICE_AGENT_ID &&
    typeof value.instructions === "string" &&
    value.instructions.trim().length > 0 &&
    Array.isArray(value.tools) &&
    value.tools.every(
      (candidate) =>
        isObject(candidate) &&
        typeof candidate.name === "string" &&
        (typeof candidate.description === "string" || candidate.description === null) &&
        isObject(candidate.parameters),
    ) &&
    isRealtimeRecordingCapability(value.recording)
  );
}

function isRealtimeRecordingCapability(value: unknown): value is RealtimeRecordingCapability {
  if (!isObject(value) || !Array.isArray(value.protocols)) return false;
  return (
    value.protocols.includes(REALTIME_RECORDING_PROTOCOL) &&
    value.transcript_capture === "client_realtime_events" &&
    value.tool_capture === "server_authoritative"
  );
}

function isRealtimeRecordingInfo(value: unknown): value is RealtimeRecordingInfo {
  if (!isObject(value)) return false;
  const sessionId = value.session_id;
  if (
    value.protocol !== REALTIME_RECORDING_PROTOCOL ||
    typeof sessionId !== "string" ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(sessionId) ||
    typeof value.run_id !== "string" ||
    !value.run_id
  ) {
    return false;
  }
  return (
    value.events_path === `/api/realtime/sessions/${sessionId}/events` &&
    value.tools_path === `/api/realtime/sessions/${sessionId}/tools/call` &&
    value.close_path === `/api/realtime/sessions/${sessionId}/close`
  );
}

function isRealtimeToken(value: unknown, config: RealtimeConfig): value is RealtimeToken {
  return (
    isObject(value) &&
    typeof value.client_secret === "string" &&
    value.client_secret.length > 0 &&
    value.model === config.model &&
    value.voice === config.voice &&
    isRealtimeRecordingInfo(value.recording)
  );
}

type RecordingEvent =
  | {
      id: string;
      kind: "opened";
      provider_session_id?: string;
    }
  | {
      id: string;
      kind: "transcript";
      speaker: "user" | "assistant";
      state: "partial" | "complete";
      item_id: string;
      content_index: number;
      sequence: number;
      text: string;
      provider_response_id?: string;
    };

interface PartialTranscript {
  id: string;
  speaker: "user" | "assistant";
  itemId: string;
  contentIndex: number;
  text: string;
  providerResponseId?: string;
}

function asRealtimeEventObject(value: unknown): Record<string, unknown> | null {
  return isObject(value) ? value : null;
}

function eventString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function eventIndex(value: unknown): number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0
    ? value
    : 0;
}

function boundedTranscript(value: string): string {
  if (value.length <= MAX_TRANSCRIPT_TEXT_LENGTH) return value;
  const suffix = " [truncated]";
  return `${value.slice(0, MAX_TRANSCRIPT_TEXT_LENGTH - suffix.length)}${suffix}`;
}

function providerToolCallCorrelation(details: unknown): {
  toolCallId: string;
  triggeringItemId?: string;
  providerResponseId?: string;
} | null {
  const detailObject = asRealtimeEventObject(details);
  const toolCall = asRealtimeEventObject(detailObject?.toolCall);
  const toolCallId = eventString(toolCall?.callId);
  if (!toolCallId) return null;
  return {
    toolCallId,
    ...(eventString(toolCall?.previousItemId)
      ? { triggeringItemId: eventString(toolCall?.previousItemId) }
      : {}),
    ...(eventString(toolCall?.responseId)
      ? { providerResponseId: eventString(toolCall?.responseId) }
      : {}),
  };
}

/**
 * Converts the supported raw Realtime transport events into the narrow
 * recording protocol. It never forwards the raw provider event object.
 *
 * The adapter owns the retry queue so a failed browser request keeps the same
 * event IDs and order when it is retried. Final transcript rows are sent at
 * once; deltas are coalesced into sparse partial checkpoints.
 */
export class RealtimeTranscriptRecorder {
  private readonly pending: RecordingEvent[] = [];
  private readonly partials = new Map<string, PartialTranscript>();
  private readonly partialTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly completed = new Set<string>();
  private drainPromise: Promise<void> | null = null;
  private closePromise: Promise<void> | null = null;
  private sequence = 0;
  private generatedId = 0;
  private opened = false;
  private closed = false;

  constructor(
    private readonly recording: RealtimeRecordingInfo,
    private readonly getToken: AccessTokenGetter,
  ) {}

  observe(event: unknown): void {
    if (this.closed) return;
    const raw = asRealtimeEventObject(event);
    if (!raw) return;
    const type = eventString(raw.type);
    if (!type) return;

    if (type === "session.created") {
      const session = asRealtimeEventObject(raw.session);
      this.enqueueOpened(
        this.providerEventId(raw, "opened"),
        eventString(session?.id) ?? eventString(raw.session_id),
      );
      return;
    }

    if (type === "conversation.item.input_audio_transcription.delta") {
      this.recordDelta("user", raw);
      return;
    }
    if (type === "conversation.item.input_audio_transcription.completed") {
      this.recordFinal("user", raw, eventString(raw.transcript));
      return;
    }
    if (type === "response.output_audio_transcript.delta") {
      this.recordDelta("assistant", raw);
      return;
    }
    if (type === "response.output_audio_transcript.done") {
      this.recordFinal("assistant", raw, eventString(raw.transcript));
      return;
    }
    if (type === "response.done") {
      this.recordResponseDone(raw);
    }
  }

  async close(reason: RecordingCloseReason, errorCode?: string): Promise<void> {
    if (this.closePromise) return this.closePromise;
    this.closed = true;
    this.flushPartialCheckpoints();
    this.closePromise = (async () => {
      // A close is still valuable if the final transcript write exhausts its
      // retry budget: it terminalises the run and makes a later stale cleanup
      // unnecessary. The pending events retain stable IDs until page teardown.
      try {
        await this.flush();
      } catch {
        // Best effort during shutdown; the close request below is independent.
      }
      try {
        await this.post(this.recording.close_path, {
          reason,
          ...(errorCode ? { error_code: errorCode } : {}),
        });
      } catch {
        // The caller has already ended media locally. Do not turn a close
        // retry into a visible error loop; server stale-session cleanup covers
        // browser/network loss windows.
      }
    })();
    return this.closePromise;
  }

  async flush(): Promise<void> {
    if (this.drainPromise) return this.drainPromise;
    this.drainPromise = this.drainPending().finally(() => {
      this.drainPromise = null;
    });
    return this.drainPromise;
  }

  private enqueueOpened(id: string, providerSessionId?: string): void {
    if (this.opened) return;
    this.opened = true;
    this.enqueue({
      id,
      kind: "opened",
      ...(providerSessionId ? { provider_session_id: providerSessionId } : {}),
    });
  }

  private recordDelta(speaker: "user" | "assistant", raw: Record<string, unknown>): void {
    const delta = eventString(raw.delta);
    const itemId = eventString(raw.item_id);
    if (!delta || !itemId) return;
    const contentIndex = eventIndex(raw.content_index);
    const key = this.transcriptKey(speaker, itemId, contentIndex);
    if (this.completed.has(key)) return;
    const existing = this.partials.get(key);
    this.partials.set(key, {
      id: this.providerEventId(raw, `${speaker}-delta`),
      speaker,
      itemId,
      contentIndex,
      text: boundedTranscript(`${existing?.text ?? ""}${delta}`),
      providerResponseId: eventString(raw.response_id) ?? existing?.providerResponseId,
    });
    if (!this.partialTimers.has(key)) {
      this.partialTimers.set(
        key,
        setTimeout(() => {
          this.partialTimers.delete(key);
          this.flushPartialCheckpoint(key);
        }, TRANSCRIPT_CHECKPOINT_DELAY_MS),
      );
    }
  }

  private recordFinal(
    speaker: "user" | "assistant",
    raw: Record<string, unknown>,
    transcript: string | undefined,
  ): void {
    const itemId = eventString(raw.item_id);
    const bounded = transcript ? boundedTranscript(transcript) : undefined;
    if (!itemId || !bounded || !bounded.trim()) return;
    const contentIndex = eventIndex(raw.content_index);
    const key = this.transcriptKey(speaker, itemId, contentIndex);
    if (this.completed.has(key)) return;
    this.clearPartial(key);
    this.completed.add(key);
    this.enqueue({
      id: this.providerEventId(raw, `${speaker}-final`),
      kind: "transcript",
      speaker,
      state: "complete",
      item_id: itemId,
      content_index: contentIndex,
      sequence: this.nextSequence(),
      text: bounded,
      ...(eventString(raw.response_id)
        ? { provider_response_id: eventString(raw.response_id) }
        : {}),
    });
  }

  private recordResponseDone(raw: Record<string, unknown>): void {
    const response = asRealtimeEventObject(raw.response);
    const outputs = response && Array.isArray(response.output) ? response.output : [];
    const responseId = eventString(raw.response_id) ?? eventString(response?.id);
    for (const output of outputs) {
      const item = asRealtimeEventObject(output);
      const itemId = eventString(item?.id);
      const content = item && Array.isArray(item.content) ? item.content : [];
      if (!itemId) continue;
      content.forEach((part, contentIndex) => {
        const audio = asRealtimeEventObject(part);
        const transcript = eventString(audio?.transcript);
        if (!transcript || !transcript.trim()) return;
        const eventId = `${this.providerEventId(raw, "response-done")}:${itemId}:${contentIndex}`.slice(0, 255);
        this.recordFinal("assistant", {
          event_id: eventId,
          item_id: itemId,
          content_index: contentIndex,
          transcript,
          ...(responseId ? { response_id: responseId } : {}),
        }, transcript);
      });
    }
  }

  private flushPartialCheckpoint(key: string): void {
    const partial = this.partials.get(key);
    if (!partial || this.completed.has(key)) return;
    this.enqueue({
      id: partial.id,
      kind: "transcript",
      speaker: partial.speaker,
      state: "partial",
      item_id: partial.itemId,
      content_index: partial.contentIndex,
      sequence: this.nextSequence(),
      text: partial.text,
      ...(partial.providerResponseId
        ? { provider_response_id: partial.providerResponseId }
        : {}),
    });
  }

  private flushPartialCheckpoints(): void {
    for (const key of this.partialTimers.keys()) {
      const timer = this.partialTimers.get(key);
      if (timer) clearTimeout(timer);
      this.partialTimers.delete(key);
      this.flushPartialCheckpoint(key);
    }
  }

  private clearPartial(key: string): void {
    const timer = this.partialTimers.get(key);
    if (timer) clearTimeout(timer);
    this.partialTimers.delete(key);
    this.partials.delete(key);
  }

  private enqueue(event: RecordingEvent): void {
    if (this.closed && event.kind !== "transcript") return;
    this.pending.push(event);
    void this.flush().catch(() => {
      // Keep the queue intact for a later provider event or shutdown flush.
    });
  }

  private async drainPending(): Promise<void> {
    while (this.pending.length > 0) {
      const batch = this.pending.slice(0, TRANSCRIPT_BATCH_SIZE);
      await this.post(this.recording.events_path, {
        protocol: REALTIME_RECORDING_PROTOCOL,
        events: batch,
      });
      this.pending.splice(0, batch.length);
    }
  }

  private async post(path: string, body: Record<string, unknown>): Promise<void> {
    let failure: Error | null = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await fetchWithAuth(
          path,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          },
          this.getToken,
        );
        if (response.ok) return;
        failure = new Error(i18n.t("realtimeVoice.recordingRequestFailedStatus", {
          status: response.status,
        }));
        // A malformed contract or closed run cannot improve on retry. Keep
        // the exact queued payload for shutdown/reconciliation diagnostics.
        if (response.status < 500 && response.status !== 429) break;
      } catch (error: unknown) {
        failure = error instanceof Error
          ? error
          : new Error(i18n.t("realtimeVoice.recordingRequestFailed"));
      }
    }
    throw failure ?? new Error(i18n.t("realtimeVoice.recordingRequestFailed"));
  }

  private transcriptKey(
    speaker: "user" | "assistant",
    itemId: string,
    contentIndex: number,
  ): string {
    return `${speaker}:${itemId}:${contentIndex}`;
  }

  private providerEventId(raw: Record<string, unknown>, prefix: string): string {
    const providerId = eventString(raw.event_id);
    if (providerId) return providerId.slice(0, 255);
    this.generatedId += 1;
    const random = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${this.generatedId}`;
    return `realtime-${prefix}-${random}`.slice(0, 255);
  }

  private nextSequence(): number {
    this.sequence += 1;
    return this.sequence;
  }
}

function messageFromUnknown(value: unknown, fallback: string): string {
  if (value instanceof Error && value.message) return value.message;
  if (isObject(value)) {
    if (typeof value.error === "string") return value.error;
    if (typeof value.detail === "string") return value.detail;
  }
  return fallback;
}

async function responseError(response: Response, fallback: string): Promise<string> {
  const payload = await response.json().catch(() => null);
  return messageFromUnknown(payload, fallback);
}

function unavailableMessage(status: number, t: TFunction): string {
  if (status === 404) {
    return t("realtimeVoice.serverUpgradeRequired");
  }
  if (status === 403) {
    return t("realtimeVoice.notEnabledForTenant");
  }
  return t("realtimeVoice.unavailable");
}

/**
 * Minimal browser WebRTC control for the Realtime voice prototype.
 *
 * The server's config response is feature discovery as well as the source of
 * truth for tool schemas. The browser tools are only backchannels: privileged
 * execution remains behind chat-bridge at /api/realtime/tools/call.
 */
export function RealtimeVoiceButton({
  tenant,
  getToken,
  compact = false,
  onRecordingCreated,
  onRecordingClosed,
}: RealtimeVoiceButtonProps) {
  const { t } = useTranslation("chat");
  const eligible = canUseRealtimeVoice(tenant);
  const backendReconnectKey = useBackendReconnectKey();
  const [state, setState] = useState<VoiceState>("checking");
  const [config, setConfig] = useState<RealtimeConfig | null>(null);
  const [message, setMessage] = useState(() => t("realtimeVoice.checking"));
  const sessionRef = useRef<CloseableSession | null>(null);
  const recorderRef = useRef<RealtimeTranscriptRecorder | null>(null);
  const toolAbortRef = useRef<AbortController | null>(null);
  const attemptRef = useRef(0);
  const onRecordingCreatedRef = useRef(onRecordingCreated);
  const onRecordingClosedRef = useRef(onRecordingClosed);
  onRecordingCreatedRef.current = onRecordingCreated;
  onRecordingClosedRef.current = onRecordingClosed;

  const closeRecording = useCallback((reason: RecordingCloseReason, errorCode?: string) => {
    const recorder = recorderRef.current;
    recorderRef.current = null;
    if (!recorder) return;
    void recorder.close(reason, errorCode).finally(() => {
      void Promise.resolve(onRecordingClosedRef.current?.()).catch(() => {
        // History refresh is cosmetic; the durable server close already won.
      });
    });
  }, []);

  const stop = useCallback((reason: RecordingCloseReason = "user_cancelled") => {
    attemptRef.current += 1;
    toolAbortRef.current?.abort();
    toolAbortRef.current = null;
    const session = sessionRef.current;
    sessionRef.current = null;
    closeRealtimeTransport(session);
    closeRecording(reason);
    setState(config ? "idle" : "unavailable");
    if (config) setMessage(t("realtimeVoice.start"));
  }, [closeRecording, config, t]);

  useEffect(() => {
    if (!eligible) {
      attemptRef.current += 1;
      toolAbortRef.current?.abort();
      toolAbortRef.current = null;
      closeRealtimeTransport(sessionRef.current);
      sessionRef.current = null;
      closeRecording("disconnected");
      setConfig(null);
      setState("unavailable");
      return;
    }

    attemptRef.current += 1;
    toolAbortRef.current?.abort();
    toolAbortRef.current = null;
    closeRealtimeTransport(sessionRef.current);
    sessionRef.current = null;
    closeRecording("disconnected");
    const controller = new AbortController();
    setConfig(null);
    setState("checking");
    setMessage(t("realtimeVoice.checking"));

    void fetchWithAuth(
      "/api/realtime/config",
      { method: "GET", headers: {}, signal: controller.signal },
      getToken,
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(unavailableMessage(response.status, t));
        }
        const payload: unknown = await response.json();
        if (!isRealtimeConfig(payload)) {
          throw new Error(
            t("realtimeVoice.recordingUpgradeRequired"),
          );
        }
        if (response.headers.get(REALTIME_RECORDING_PROXY_HEADER) !== "1") {
          throw new Error(t("realtimeVoice.bridgeUpgradeRequired"));
        }
        if (!controller.signal.aborted) {
          setConfig(payload);
          setState("idle");
          setMessage(t("realtimeVoice.start"));
        }
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setConfig(null);
        setState("unavailable");
        setMessage(messageFromUnknown(error, t("realtimeVoice.unavailableGeneric")));
      });

    return () => controller.abort();
  }, [backendReconnectKey, closeRecording, eligible, getToken, t]);

  useEffect(
    () => () => {
      attemptRef.current += 1;
      toolAbortRef.current?.abort();
      closeRealtimeTransport(sessionRef.current);
      sessionRef.current = null;
      closeRecording("disconnected");
    },
    [closeRecording],
  );

  const start = useCallback(async () => {
    if (!config || state !== "idle") return;

    const attempt = attemptRef.current + 1;
    attemptRef.current = attempt;
    setState("connecting");
    setMessage(t("realtimeVoice.connecting"));

    try {
      const tokenResponse = await fetchWithAuth(
        "/api/realtime/token",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ recording_protocol: REALTIME_RECORDING_PROTOCOL }),
        },
        getToken,
      );
      if (!tokenResponse.ok) {
        if (attemptRef.current !== attempt) return;
        const tokenError = tokenResponse.status === 403 || tokenResponse.status === 404
          ? unavailableMessage(tokenResponse.status, t)
          : await responseError(tokenResponse, t("realtimeVoice.couldNotStart"));
        if (attemptRef.current !== attempt) return;
        if (
          tokenResponse.status === 403 ||
          tokenResponse.status === 404 ||
          tokenResponse.status === 503
        ) {
          setConfig(null);
          setState("unavailable");
          setMessage(tokenError);
          return;
        }
        throw new Error(tokenError);
      }
      const tokenPayload: unknown = await tokenResponse.json();
      if (!isRealtimeToken(tokenPayload, config)) {
        throw new Error(t("realtimeVoice.invalidToken"));
      }
      const token = tokenPayload;
      const recorder = new RealtimeTranscriptRecorder(token.recording, getToken);
      recorderRef.current = recorder;
      // The token response proves the server-owned recording envelope exists.
      // Refresh the session list now, rather than making its appearance wait
      // until the caller later closes the microphone.
      void Promise.resolve(onRecordingCreatedRef.current?.()).catch(() => {
        // History refresh is cosmetic; the session was already provisioned.
      });
      if (attemptRef.current !== attempt) {
        closeRecording("disconnected");
        return;
      }

      // Loaded only after authenticated feature discovery and a successful
      // ephemeral-token mint. The SDK's default browser transport is WebRTC.
      const { RealtimeAgent, RealtimeSession, tool } = await import(
        "@openai/agents/realtime"
      );
      if (attemptRef.current !== attempt) {
        if (recorderRef.current === recorder) closeRecording("disconnected");
        return;
      }

      const toolAbort = new AbortController();
      toolAbortRef.current = toolAbort;
      const tools = [
        ...config.tools.map((schema) =>
          tool({
            name: schema.name,
            description: schema.description ?? `Run ${schema.name} on the Layer Nine server.`,
            // The schemas are runtime-derived, so TypeScript cannot infer the
            // generic input type expected by tool(); config validation above
            // and the trusted server are the runtime boundary.
            parameters: schema.parameters as never,
            // Agno's generated schemas are valid JSON Schema but are not
            // normalized to the SDK's strict-schema subset.
            strict: false,
            execute: async (input: unknown, _context?: unknown, details?: unknown) => {
              const argumentsObject = isObject(input) ? input : {};
              const correlation = providerToolCallCorrelation(details);
              if (!correlation) {
                // Parallel calls must be identified by the provider's call
                // ID. Never guess based on the tool name or invocation time.
                throw new Error(t("realtimeVoice.missingProviderId"));
              }
              const response = await fetchWithAuth(
                token.recording.tools_path,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    tool_name: schema.name,
                    arguments: argumentsObject,
                    tool_call_id: correlation.toolCallId,
                    ...(correlation.triggeringItemId
                      ? { triggering_item_id: correlation.triggeringItemId }
                      : {}),
                    ...(correlation.providerResponseId
                      ? { provider_response_id: correlation.providerResponseId }
                      : {}),
                  }),
                  signal: toolAbort.signal,
                },
                getToken,
              );
              if (!response.ok) {
                throw new Error(
                  await responseError(response, t("realtimeVoice.toolFailed", { tool: schema.name })),
                );
              }
              const result = (await response.json()) as ToolResult;
              // RealtimeSession converts this return value into the matching
              // response.function_call_output and resumes the model response.
              return JSON.stringify(result);
            },
          }),
        ),
        // This is intentionally a browser-only tool. It follows the exact
        // same shutdown path as the Cancel button and is never sent through
        // /api/realtime/tools/call or advertised as an Agno backend tool.
        tool({
          name: END_CONVERSATION_TOOL_NAME,
          description: "End the current realtime voice conversation.",
          parameters: {
            type: "object",
            properties: {},
            additionalProperties: false,
          } as never,
          strict: false,
          execute: async () => {
            stop("end_conversation");
            return JSON.stringify({
              status: "ok",
              result: "The realtime voice conversation has ended.",
            });
          },
        }),
      ];

      const agent = new RealtimeAgent({
        name: "Layer Nine Voice",
        instructions: config.instructions,
        tools,
      });
      const session = new RealtimeSession(agent, {
        model: config.model,
        tracingDisabled: true,
        config: {
          outputModalities: ["audio"],
          parallelToolCalls: true,
          audio: { output: { voice: config.voice } },
        },
      });
      sessionRef.current = session;

      // The pinned SDK's raw transport events retain the provider event and
      // function correlation IDs that its higher-level UI callbacks omit.
      // Subscribe before connect so session.created and the first transcript
      // cannot race past the recorder.
      const transport = session.transport as unknown as {
        on?: (eventName: string, listener: (event: unknown) => void) => unknown;
      };
      transport.on?.("*", (event: unknown) => recorder.observe(event));

      session.on("error", (error: unknown) => {
        if (sessionRef.current !== session) return;
        sessionRef.current = null;
        toolAbortRef.current?.abort();
        toolAbortRef.current = null;
        closeRealtimeTransport(session);
        closeRecording("error", "realtime_session_error");
        setState("idle");
        setMessage(messageFromUnknown(error, t("realtimeVoice.sessionFailed")));
      });
      session.transport.on("connection_change", (connectionState: string) => {
        if (connectionState !== "disconnected" || sessionRef.current !== session) return;
        sessionRef.current = null;
        toolAbortRef.current?.abort();
        toolAbortRef.current = null;
        closeRecording("disconnected", "realtime_disconnected");
        setState("idle");
        setMessage(t("realtimeVoice.disconnected"));
      });

      await session.connect({ apiKey: token.client_secret });
      if (attemptRef.current !== attempt || sessionRef.current !== session) {
        closeRealtimeTransport(session);
        if (recorderRef.current === recorder) closeRecording("disconnected");
        return;
      }
      setState("active");
      setMessage(t("realtimeVoice.cancel"));
    } catch (error: unknown) {
      if (attemptRef.current !== attempt) return;
      toolAbortRef.current?.abort();
      toolAbortRef.current = null;
      closeRealtimeTransport(sessionRef.current);
      sessionRef.current = null;
      closeRecording("error", "realtime_connect_failed");
      setState("idle");
      setMessage(messageFromUnknown(error, t("realtimeVoice.couldNotStart")));
    }
  }, [closeRecording, config, getToken, state, stop, t]);

  if (!eligible) return null;

  const stoppingEnabled = state === "connecting" || state === "active";
  const disabled = state === "checking" || state === "unavailable";
  const hasError = state === "unavailable" || (state === "idle" && message !== t("realtimeVoice.start"));

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <Button
              type="button"
              variant="ghost"
              size={stoppingEnabled && !compact ? "sm" : "icon"}
              className={stoppingEnabled && !compact ? "h-7 gap-1 px-2 text-destructive" : stoppingEnabled ? "size-7 text-destructive" : "size-7"}
              disabled={disabled}
              aria-label={message}
              aria-pressed={state === "active"}
              onClick={stoppingEnabled ? () => stop() : () => void start()}
            >
              {state === "connecting" ? (
                <Loader2 className="size-4 animate-spin" />
              ) : stoppingEnabled ? (
                <X className="size-4" />
              ) : (
                <Mic className={hasError ? "size-4 text-destructive" : "size-4"} />
              )}
              {stoppingEnabled && <span className={compact ? "sr-only" : undefined}>{t("common:actions.cancel")}</span>}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{message}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
